import { useState } from 'react'
import {
  useChecklistTemplates,
  useChecklistTemplate,
  useCreateChecklistTemplate,
  useUpdateChecklistTemplate,
  useDeleteChecklistTemplate
} from '../hooks/useChecklists'
import {
  ModalShell, Field, inputClass, CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import type { ChecklistTemplate, ChecklistTemplateBody } from '../types'

// AdminChecklistTemplates - admin-only page for managing the template
// catalogue. Lists active templates with item count, opens a full-screen
// editor on click. The editor supports per-row inline edit, drag-style
// reorder via up/down buttons (no DnD library in v1), section combobox
// fed by existing sections in the template.

export function AdminChecklistTemplates() {
  const { data: templates, isLoading, error } = useChecklistTemplates()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Checklist Templates</h1>
          <p className="text-ink-soft">
            Reusable templates that event managers apply to populate the per-event Checklists tab.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New template
        </button>
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading templates…</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load templates</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {templates && templates.length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f5edd5', color: '#9b7f2a' }}
          >
            <Icon name="check-circle" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No templates yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Templates capture the standard set of tasks for an event type
            (Gala, Christmas, Family event). Create your first one and event
            managers can apply it from the Checklists tab.
          </p>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
          >
            + Create the first template
          </button>
        </div>
      )}

      {templates && templates.length > 0 && (
        <section className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Templates</h2>
            <span className="text-xs text-ink-soft font-mono">
              {templates.length} template{templates.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setEditingId(t.id)}
                className="w-full text-left px-4 py-3 flex items-start gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="check-circle" size={16} className="text-green flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{t.name}</div>
                  {t.description && (
                    <div className="text-xs text-ink-soft mt-0.5 truncate">{t.description}</div>
                  )}
                </div>
                <div className="text-xs text-ink-soft font-mono whitespace-nowrap">
                  {t.itemCount} item{t.itemCount === 1 ? '' : 's'}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {creating && (
        <TemplateEditorModal
          templateId={null}
          onClose={() => setCreating(false)}
        />
      )}
      {editingId && (
        <TemplateEditorModal
          templateId={editingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  )
}

// ---- Editor modal ----------------------------------------------------------

interface EditorProps {
  templateId: number | null
  onClose: () => void
}

function TemplateEditorModal({ templateId, onClose }: EditorProps) {
  const isEditing = templateId != null
  const detail = useChecklistTemplate(templateId ?? -1)
  const createMut = useCreateChecklistTemplate()
  const updateMut = useUpdateChecklistTemplate(templateId ?? -1)
  const deleteMut = useDeleteChecklistTemplate()

  // For new templates we don't wait on detail load.
  const loaded = !isEditing || detail.data != null
  if (!loaded) {
    return (
      <ModalShell
        title="Loading template…"
        onClose={onClose}
        body={<p className="text-sm text-ink-soft font-mono">Fetching template…</p>}
      />
    )
  }

  return (
    <TemplateEditorForm
      isEditing={isEditing}
      initial={detail.data ?? undefined}
      saving={createMut.isPending || updateMut.isPending}
      deleting={deleteMut.isPending}
      serverError={(createMut.error ?? updateMut.error ?? deleteMut.error) as Error | null}
      onClose={onClose}
      onSave={(body) => {
        if (isEditing && templateId != null) {
          updateMut.mutate(body, { onSuccess: () => onClose() })
        } else {
          createMut.mutate(body, { onSuccess: () => onClose() })
        }
      }}
      onDelete={
        isEditing && templateId != null
          ? () => {
              const ok = window.confirm(
                `Delete "${detail.data?.name}"? Templates are soft-deleted; existing applied checklists keep working.`
              )
              if (!ok) return
              deleteMut.mutate(templateId, { onSuccess: () => onClose() })
            }
          : undefined
      }
    />
  )
}

interface EditableItem {
  section: string
  title: string
  description: string | null
  defaultOwnerRole: string | null
  daysBeforeEvent: number | null
  sortOrder: number
}

function templateToEditable(t: ChecklistTemplate | undefined): EditableItem[] {
  return (t?.items ?? []).map((i) => ({
    section: i.section,
    title: i.title,
    description: i.description,
    defaultOwnerRole: i.defaultOwnerRole,
    daysBeforeEvent: i.daysBeforeEvent,
    sortOrder: i.sortOrder
  }))
}

interface FormProps {
  isEditing: boolean
  initial: ChecklistTemplate | undefined
  saving: boolean
  deleting: boolean
  serverError: Error | null
  onClose: () => void
  onSave: (body: ChecklistTemplateBody) => void
  onDelete?: () => void
}

function TemplateEditorForm({
  isEditing, initial, saving, deleting, serverError, onClose, onSave, onDelete
}: FormProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [items, setItems] = useState<EditableItem[]>(templateToEditable(initial))

  const trimmedName = name.trim()
  const isValid =
    trimmedName.length > 0 &&
    items.every((it) => it.title.trim().length > 0 && it.section.trim().length > 0)

  function handleSubmit() {
    if (!isValid) return
    onSave({
      name: trimmedName,
      description: description.trim().length === 0 ? null : description,
      items: items.map((it, i) => ({
        section: it.section.trim(),
        title: it.title.trim(),
        description: it.description?.trim() === '' ? null : it.description,
        defaultOwnerRole: it.defaultOwnerRole?.trim() === '' ? null : it.defaultOwnerRole,
        daysBeforeEvent: it.daysBeforeEvent,
        sortOrder: i * 10
      }))
    })
  }

  function updateItem(idx: number, patch: Partial<EditableItem>) {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  }
  function addItem() {
    setItems((cur) => [
      ...cur,
      {
        section: cur[cur.length - 1]?.section ?? 'Pre-event',
        title: '',
        description: null,
        defaultOwnerRole: null,
        daysBeforeEvent: null,
        sortOrder: cur.length * 10
      }
    ])
  }
  function removeItem(idx: number) {
    setItems((cur) => cur.filter((_, i) => i !== idx))
  }
  function moveItem(idx: number, dir: -1 | 1) {
    setItems((cur) => {
      const next = [...cur]
      const t = idx + dir
      if (t < 0 || t >= next.length) return cur
      const tmp = next[idx]!
      next[idx] = next[t]!
      next[t] = tmp
      return next
    })
  }

  const body = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Standard Event"
            maxLength={120}
            className={inputClass(name.length > 0 && trimmedName.length === 0)}
          />
        </Field>
        <Field label="Description (optional)">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Baseline checklist for any event."
            maxLength={500}
            className={inputClass(false)}
          />
        </Field>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
            Items {items.length > 0 && <span className="ml-1 font-mono text-ink-soft/70">({items.length})</span>}
          </span>
          <button
            type="button"
            onClick={addItem}
            className="text-xs font-semibold text-green hover:underline"
          >
            + Add item
          </button>
        </div>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {items.length === 0 && (
            <p className="text-xs italic text-ink-soft py-3 text-center bg-parchment/40 rounded border border-line">
              No items yet. Click <span className="font-semibold">+ Add item</span> to start.
            </p>
          )}
          {items.map((it, i) => (
            <div
              key={i}
              className="border border-line rounded-md bg-white shadow-sm"
            >
              {/* Item header strip — section pill + ordering / remove
                  controls. Visually distinct from the body so the row
                  reads as "this item, in this section." */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-parchment/60 border-b border-line rounded-t-md">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft">#{i + 1}</span>
                <input
                  type="text"
                  value={it.section}
                  onChange={(e) => updateItem(i, { section: e.target.value })}
                  placeholder="Section (e.g. Pre-event)"
                  maxLength={80}
                  className={`flex-1 max-w-[200px] border rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 ${
                    it.section.trim().length === 0
                      ? 'border-oxblood focus:border-oxblood focus:ring-oxblood/30'
                      : 'border-line focus:border-green focus:ring-green/30'
                  }`}
                />
                <div className="ml-auto flex gap-1">
                  <button
                    type="button"
                    onClick={() => moveItem(i, -1)}
                    disabled={i === 0}
                    aria-label="Move up"
                    title="Move up"
                    className="w-7 h-7 rounded border border-line bg-white text-ink-soft hover:text-ink disabled:opacity-40"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(i, 1)}
                    disabled={i === items.length - 1}
                    aria-label="Move down"
                    title="Move down"
                    className="w-7 h-7 rounded border border-line bg-white text-ink-soft hover:text-ink disabled:opacity-40"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    aria-label="Remove item"
                    title="Remove"
                    className="w-7 h-7 rounded border border-line bg-white text-oxblood hover:bg-oxblood-soft"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Item body — title at top (the dominant field), then
                  metadata in a uniform 3-col grid below. */}
              <div className="p-3 space-y-2">
                <input
                  type="text"
                  value={it.title}
                  onChange={(e) => updateItem(i, { title: e.target.value })}
                  placeholder="Item title"
                  maxLength={200}
                  className={inputClass(it.title.trim().length === 0)}
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={it.defaultOwnerRole ?? ''}
                    onChange={(e) => updateItem(i, { defaultOwnerRole: e.target.value })}
                    placeholder="Default owner role"
                    maxLength={80}
                    className={`border border-line rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:border-green focus:ring-green/30`}
                  />
                  <input
                    type="number"
                    value={it.daysBeforeEvent ?? ''}
                    onChange={(e) =>
                      updateItem(i, {
                        daysBeforeEvent: e.target.value === '' ? null : Number(e.target.value)
                      })
                    }
                    placeholder="Days before event"
                    className={`border border-line rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:border-green focus:ring-green/30`}
                  />
                  <input
                    type="text"
                    value={it.description ?? ''}
                    onChange={(e) => updateItem(i, { description: e.target.value })}
                    placeholder="Notes (optional)"
                    className={`border border-line rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:border-green focus:ring-green/30`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {serverError && (
        <ServerError title="Could not save" error={serverError} />
      )}
    </div>
  )

  const footer = (
    <>
      {onDelete && (
        <DangerButton onClick={onDelete} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete'}
        </DangerButton>
      )}
      <CancelButton onClick={onClose} disabled={saving} />
      <PrimaryButton onClick={handleSubmit} disabled={!isValid || saving}>
        {saving ? 'Saving…' : isEditing ? 'Save template' : 'Create template'}
      </PrimaryButton>
    </>
  )

  return (
    <ModalShell
      title={isEditing ? 'Edit checklist template' : 'New checklist template'}
      onClose={onClose}
      body={body}
      footer={footer}
      size="2xl"
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
    />
  )
}
